import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { fbt } from 'fbtee'
import { useSearchParams } from 'react-router-dom'
import { ensureAnonAuth } from '../lib/firebase'
import { useAuth } from '../hooks/useAuth'
import { useFriendships } from '../hooks/useFriendships'
import { resolveInvite, resolveInviterName, isBefriendOffered, markBefriendOffered } from '../lib/invites'
import { recordSignupSource } from '../lib/signupSource'
import { peekPendingIntent, takePendingIntent, setPendingIntent } from '../lib/pendingIntent'
import { track } from '../lib/analytics'
import { log } from '../lib/logger'
import type { Invite } from '../types'

// The invite gate. Mounted once inside GuestGuardProvider, it reads ?invite=<id>
// off ANY route and does the route-agnostic parts of a share link: give a guest
// an anonymous session so they can read the shared content, record attribution,
// and — after a guest signs up from the link — offer to add the inviter as a
// friend (a toast with an "Add friend" action, never automatic). Everything
// route-specific (rendering the shared schedule, joining a shared group) lives on
// the destination page. The param is kept in the URL (view-first) so a shared
// schedule survives a reload and a lazily-mounted destination.
export function InviteAttachment() {
  const [searchParams] = useSearchParams()
  const inviteParam = searchParams.get('invite')
  const { user, signedInUid, loading: authLoading } = useAuth()
  const { sendRequest } = useFriendships(signedInUid)

  const [invite, setInvite] = useState<Invite | null>(null)
  const resolvedRef = useRef<string | null>(null)
  const wasSignedOutRef = useRef(false)
  const offeredForRef = useRef<string | null>(null)

  // Resolve once (public read, no session needed). Record attribution and stash
  // the id so the befriend offer survives a sign-up on any route.
  useEffect(() => {
    if (!inviteParam || resolvedRef.current === inviteParam) return
    resolvedRef.current = inviteParam
    recordSignupSource('share_link')
    resolveInvite(inviteParam)
      .then(resolved => {
        if (!resolved || resolved.revoked) return
        track('invite_opened', { kind: resolved.kind })
        setPendingIntent('invite', resolved.id)
        setInvite(resolved)
      })
      .catch(err => log('invite', 'resolve failed', { inviteId: inviteParam, error: String(err) }))
  }, [inviteParam])

  // Give a guest a session so they can read the shared content. Also marks that we
  // arrived signed-out on this invite, which is what turns a later sign-in into a
  // "converted from the link" that deserves the befriend offer.
  useEffect(() => {
    if (authLoading) return
    if (invite && !signedInUid) {
      wasSignedOutRef.current = true
      if (!user) ensureAnonAuth().catch(err => log('auth', 'ensureAnonAuth failed', { error: String(err) }))
    }
  }, [invite, authLoading, signedInUid, user])

  // After a guest converts (was signed-out on this invite, now signed in), offer
  // to add the inviter as a friend. Once per invite per device; skipped when the
  // viewer is the owner. A freshly-converted account can't already be friends, so
  // there's no "already friends" error to guard against.
  useEffect(() => {
    if (authLoading || !signedInUid || !wasSignedOutRef.current) return
    if (offeredForRef.current === signedInUid) return
    offeredForRef.current = signedInUid

    let cancelled = false
    ;(async () => {
      let inv = invite
      if (!inv) {
        const pendingId = peekPendingIntent('invite')
        if (!pendingId) return
        inv = await resolveInvite(pendingId)
        if (cancelled || !inv) return
      }
      takePendingIntent('invite')
      if (inv.revoked || inv.ownerId === signedInUid || isBefriendOffered(inv.id)) return
      markBefriendOffered(inv.id)

      const inviterName = await resolveInviterName(inv.ownerId)
      if (cancelled) return
      const name =
        inviterName ||
        (fbt('a Faro user', 'Fallback inviter name in the befriend toast') as unknown as string)
      const ownerId = inv.ownerId
      toast(
        fbt('Add ' + fbt.param('name', name) + ' as a friend?', 'Toast offering to befriend the inviter after signing up from a shared link'),
        {
          action: {
            label: fbt('Add', 'Action button to send the inviter a friend request from the befriend toast') as unknown as string,
            onClick: () => {
              sendRequest(ownerId, 'share_link').catch(() => {})
            },
          },
        },
      )
    })().catch(err => log('invite', 'befriend offer failed', { error: String(err) }))

    return () => {
      cancelled = true
    }
  }, [authLoading, signedInUid, invite, sendRequest])

  return null
}
